/**
 * Safari / Firefox fallback for CSS corner-shape: squircle.
 * Chrome uses native corner-shape; this clip-path only runs where that's missing.
 * preserveSmoothing is off so 24px on short buttons stays a squircle, not a pill.
 */
(function () {
  'use strict';

  if (
    typeof CSS !== 'undefined' &&
    CSS.supports &&
    (CSS.supports('corner-shape', 'squircle') || CSS.supports('corner-shape', 'superellipse(2)'))
  ) {
    return;
  }

  var SMOOTHING = 0.6;
  var DEFAULT_RADIUS = 24;

  var SELECTORS = [
    '.variant-option__select-wrapper',
    '.quantity-selector',
    '.product-form-buttons .button',
    '.product-form-buttons button',
    '.product-form__submit',
    'button[name="add"]',
    '.buy-buttons .button',
    '.add-to-cart-button',
    '.addons-card',
    '.custom-checkbox-box',
    '.addon-price',
    '[class*="cs-container-"]',
    '[class*="cs-action-btn-"]',
    '[class*="cs-discount-badge-"]',
    '[class*="cs-discount-tag-"]',
    '[class*="cs-image-wrap-"]',
    '.custom-color-selector',
    '.sticky-add-to-cart__button',
    '.zoom-sticky-button',
    '.dialog-thumbnails-list__thumbnail',
    '.slideshow-controls__thumbnail',
    '.rhow-size-chart-floating-trigger',
    '.rhow-pabis-dialog',
    '.rhow-pabis-field input',
    '.rhow-pabis-dialog__submit',
    '.rhow-pabis-dialog__success-close',
    '.rhow-pabis-notify-button',
    '.rhow-pabis-preorder',
  ].join(',');

  var adjacentsByCorner = {
    topLeft: [
      { corner: 'topRight', side: 'top' },
      { corner: 'bottomLeft', side: 'left' },
    ],
    topRight: [
      { corner: 'topLeft', side: 'top' },
      { corner: 'bottomRight', side: 'right' },
    ],
    bottomLeft: [
      { corner: 'bottomRight', side: 'bottom' },
      { corner: 'topLeft', side: 'left' },
    ],
    bottomRight: [
      { corner: 'bottomLeft', side: 'bottom' },
      { corner: 'topRight', side: 'right' },
    ],
  };

  function n(v) {
    return Number(v).toFixed(4);
  }

  function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
  }

  function distributeAndNormalize(opts) {
    var roundingAndSmoothingBudgetMap = {
      topLeft: -1,
      topRight: -1,
      bottomLeft: -1,
      bottomRight: -1,
    };
    var cornerRadiusMap = {
      topLeft: opts.topLeftCornerRadius,
      topRight: opts.topRightCornerRadius,
      bottomLeft: opts.bottomLeftCornerRadius,
      bottomRight: opts.bottomRightCornerRadius,
    };
    var width = opts.width;
    var height = opts.height;

    Object.keys(cornerRadiusMap)
      .map(function (key) {
        return { key: key, radius: cornerRadiusMap[key] };
      })
      .sort(function (a, b) {
        return b.radius - a.radius;
      })
      .forEach(function (item) {
        var corner = item.key;
        var radius = item.radius;
        var adjacents = adjacentsByCorner[corner];
        var budgets = adjacents.map(function (adjacent) {
          var adjacentCornerRadius = cornerRadiusMap[adjacent.corner];
          if (radius === 0 && adjacentCornerRadius === 0) return 0;
          var adjacentCornerBudget = roundingAndSmoothingBudgetMap[adjacent.corner];
          var sideLength =
            adjacent.side === 'top' || adjacent.side === 'bottom' ? width : height;
          if (adjacentCornerBudget >= 0) {
            return sideLength - roundingAndSmoothingBudgetMap[adjacent.corner];
          }
          return (radius / (radius + adjacentCornerRadius)) * sideLength;
        });
        var budget = Math.min.apply(null, budgets);
        roundingAndSmoothingBudgetMap[corner] = budget;
        cornerRadiusMap[corner] = Math.min(radius, budget);
      });

    return {
      topLeft: {
        radius: cornerRadiusMap.topLeft,
        roundingAndSmoothingBudget: roundingAndSmoothingBudgetMap.topLeft,
      },
      topRight: {
        radius: cornerRadiusMap.topRight,
        roundingAndSmoothingBudget: roundingAndSmoothingBudgetMap.topRight,
      },
      bottomLeft: {
        radius: cornerRadiusMap.bottomLeft,
        roundingAndSmoothingBudget: roundingAndSmoothingBudgetMap.bottomLeft,
      },
      bottomRight: {
        radius: cornerRadiusMap.bottomRight,
        roundingAndSmoothingBudget: roundingAndSmoothingBudgetMap.bottomRight,
      },
    };
  }

  function getPathParamsForCorner(opts) {
    var cornerRadius = opts.cornerRadius;
    var cornerSmoothing = opts.cornerSmoothing;
    var preserveSmoothing = opts.preserveSmoothing;
    var roundingAndSmoothingBudget = opts.roundingAndSmoothingBudget;
    var p = (1 + cornerSmoothing) * cornerRadius;

    if (!preserveSmoothing) {
      var maxCornerSmoothing = roundingAndSmoothingBudget / cornerRadius - 1;
      cornerSmoothing = Math.min(cornerSmoothing, maxCornerSmoothing);
      p = Math.min(p, roundingAndSmoothingBudget);
    }

    var arcMeasure = 90 * (1 - cornerSmoothing);
    var arcSectionLength = Math.sin(toRadians(arcMeasure / 2)) * cornerRadius * Math.sqrt(2);
    var angleAlpha = (90 - arcMeasure) / 2;
    var p3ToP4Distance = cornerRadius * Math.tan(toRadians(angleAlpha / 2));
    var angleBeta = 45 * cornerSmoothing;
    var c = p3ToP4Distance * Math.cos(toRadians(angleBeta));
    var d = c * Math.tan(toRadians(angleBeta));
    var b = (p - arcSectionLength - c - d) / 3;
    var a = 2 * b;

    if (preserveSmoothing && p > roundingAndSmoothingBudget) {
      var p1ToP3MaxDistance = roundingAndSmoothingBudget - d - arcSectionLength - c;
      var maxB = p1ToP3MaxDistance - p1ToP3MaxDistance / 6;
      b = Math.min(b, maxB);
      a = p1ToP3MaxDistance - b;
      p = Math.min(p, roundingAndSmoothingBudget);
    }

    return {
      a: a,
      b: b,
      c: c,
      d: d,
      p: p,
      arcSectionLength: arcSectionLength,
      cornerRadius: cornerRadius,
    };
  }

  function drawTopRightPath(p) {
    if (!p.cornerRadius) return 'l ' + n(p.p) + ' 0';
    return (
      'c ' + n(p.a) + ' 0 ' + n(p.a + p.b) + ' 0 ' + n(p.a + p.b + p.c) + ' ' + n(p.d) +
      ' a ' + n(p.cornerRadius) + ' ' + n(p.cornerRadius) + ' 0 0 1 ' + n(p.arcSectionLength) + ' ' + n(p.arcSectionLength) +
      ' c ' + n(p.d) + ' ' + n(p.c) + ' ' + n(p.d) + ' ' + n(p.b + p.c) + ' ' + n(p.d) + ' ' + n(p.a + p.b + p.c)
    );
  }

  function drawBottomRightPath(p) {
    if (!p.cornerRadius) return 'l 0 ' + n(p.p);
    return (
      'c 0 ' + n(p.a) + ' 0 ' + n(p.a + p.b) + ' ' + n(-p.d) + ' ' + n(p.a + p.b + p.c) +
      ' a ' + n(p.cornerRadius) + ' ' + n(p.cornerRadius) + ' 0 0 1 ' + n(-p.arcSectionLength) + ' ' + n(p.arcSectionLength) +
      ' c ' + n(-p.c) + ' ' + n(p.d) + ' ' + n(-(p.b + p.c)) + ' ' + n(p.d) + ' ' + n(-(p.a + p.b + p.c)) + ' ' + n(p.d)
    );
  }

  function drawBottomLeftPath(p) {
    if (!p.cornerRadius) return 'l ' + n(-p.p) + ' 0';
    return (
      'c ' + n(-p.a) + ' 0 ' + n(-(p.a + p.b)) + ' 0 ' + n(-(p.a + p.b + p.c)) + ' ' + n(-p.d) +
      ' a ' + n(p.cornerRadius) + ' ' + n(p.cornerRadius) + ' 0 0 1 ' + n(-p.arcSectionLength) + ' ' + n(-p.arcSectionLength) +
      ' c ' + n(-p.d) + ' ' + n(-p.c) + ' ' + n(-p.d) + ' ' + n(-(p.b + p.c)) + ' ' + n(-p.d) + ' ' + n(-(p.a + p.b + p.c))
    );
  }

  function drawTopLeftPath(p) {
    if (!p.cornerRadius) return 'l 0 ' + n(-p.p);
    return (
      'c 0 ' + n(-p.a) + ' 0 ' + n(-(p.a + p.b)) + ' ' + n(p.d) + ' ' + n(-(p.a + p.b + p.c)) +
      ' a ' + n(p.cornerRadius) + ' ' + n(p.cornerRadius) + ' 0 0 1 ' + n(p.arcSectionLength) + ' ' + n(-p.arcSectionLength) +
      ' c ' + n(p.c) + ' ' + n(-p.d) + ' ' + n(p.b + p.c) + ' ' + n(-p.d) + ' ' + n(p.a + p.b + p.c) + ' ' + n(-p.d)
    );
  }

  function getSVGPathFromPathParams(opts) {
    var width = opts.width;
    var height = opts.height;
    var tr = opts.topRightPathParams;
    var br = opts.bottomRightPathParams;
    var bl = opts.bottomLeftPathParams;
    var tl = opts.topLeftPathParams;
    return (
      'M ' + n(width - tr.p) + ' 0 ' +
      drawTopRightPath(tr) +
      ' L ' + n(width) + ' ' + n(height - br.p) + ' ' +
      drawBottomRightPath(br) +
      ' L ' + n(bl.p) + ' ' + n(height) + ' ' +
      drawBottomLeftPath(bl) +
      ' L 0 ' + n(tl.p) + ' ' +
      drawTopLeftPath(tl) +
      ' Z'
    );
  }

  function getSvgPath(opts) {
    var cornerRadius = opts.cornerRadius || 0;
    var topLeftCornerRadius = opts.topLeftCornerRadius != null ? opts.topLeftCornerRadius : cornerRadius;
    var topRightCornerRadius = opts.topRightCornerRadius != null ? opts.topRightCornerRadius : cornerRadius;
    var bottomLeftCornerRadius = opts.bottomLeftCornerRadius != null ? opts.bottomLeftCornerRadius : cornerRadius;
    var bottomRightCornerRadius = opts.bottomRightCornerRadius != null ? opts.bottomRightCornerRadius : cornerRadius;
    var cornerSmoothing = opts.cornerSmoothing;
    var width = opts.width;
    var height = opts.height;
    var preserveSmoothing = !!opts.preserveSmoothing;

    if (
      topLeftCornerRadius === topRightCornerRadius &&
      topRightCornerRadius === bottomRightCornerRadius &&
      bottomRightCornerRadius === bottomLeftCornerRadius
    ) {
      var budget = Math.min(width, height) / 2;
      var r = Math.min(topLeftCornerRadius, budget);
      var pathParams = getPathParamsForCorner({
        cornerRadius: r,
        cornerSmoothing: cornerSmoothing,
        preserveSmoothing: preserveSmoothing,
        roundingAndSmoothingBudget: budget,
      });
      return getSVGPathFromPathParams({
        width: width,
        height: height,
        topLeftPathParams: pathParams,
        topRightPathParams: pathParams,
        bottomLeftPathParams: pathParams,
        bottomRightPathParams: pathParams,
      });
    }

    var corners = distributeAndNormalize({
      topLeftCornerRadius: topLeftCornerRadius,
      topRightCornerRadius: topRightCornerRadius,
      bottomRightCornerRadius: bottomRightCornerRadius,
      bottomLeftCornerRadius: bottomLeftCornerRadius,
      width: width,
      height: height,
    });

    function paramsFor(corner) {
      return getPathParamsForCorner({
        cornerSmoothing: cornerSmoothing,
        preserveSmoothing: preserveSmoothing,
        cornerRadius: corner.radius,
        roundingAndSmoothingBudget: corner.roundingAndSmoothingBudget,
      });
    }

    return getSVGPathFromPathParams({
      width: width,
      height: height,
      topLeftPathParams: paramsFor(corners.topLeft),
      topRightPathParams: paramsFor(corners.topRight),
      bottomRightPathParams: paramsFor(corners.bottomRight),
      bottomLeftPathParams: paramsFor(corners.bottomLeft),
    });
  }

  function parseRadii(el) {
    var style = getComputedStyle(el);
    var tl = parseFloat(style.borderTopLeftRadius) || 0;
    var tr = parseFloat(style.borderTopRightRadius) || 0;
    var br = parseFloat(style.borderBottomRightRadius) || 0;
    var bl = parseFloat(style.borderBottomLeftRadius) || 0;

    if (tl + tr + br + bl === 0) {
      return null;
    }
    return { tl: tl, tr: tr, br: br, bl: bl };
  }

  function applySmooth(el) {
    var rect = el.getBoundingClientRect();
    var width = Math.round(rect.width * 100) / 100;
    var height = Math.round(rect.height * 100) / 100;
    if (width < 2 || height < 2) return;

    var radii = parseRadii(el);
    if (!radii) return;
    var halfMin = Math.min(width, height) / 2;
    radii.tl = Math.min(radii.tl, halfMin);
    radii.tr = Math.min(radii.tr, halfMin);
    radii.br = Math.min(radii.br, halfMin);
    radii.bl = Math.min(radii.bl, halfMin);

    var path = getSvgPath({
      width: width,
      height: height,
      cornerSmoothing: SMOOTHING,
      preserveSmoothing: false,
      topLeftCornerRadius: radii.tl,
      topRightCornerRadius: radii.tr,
      bottomRightCornerRadius: radii.br,
      bottomLeftCornerRadius: radii.bl,
    });

    var clip = "path('" + path + "')";
    el.style.clipPath = clip;
    el.style.webkitClipPath = clip;
    el.dataset.iosSmoothApplied = '1';
  }

  function collectTargets(root) {
    var scope = root || document;
    var nodes = [];
    try {
      nodes = Array.prototype.slice.call(scope.querySelectorAll(SELECTORS));
    } catch (e) {
      return [];
    }
    return nodes.filter(function (el) {
      return el.getClientRects().length > 0;
    });
  }

  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            applySmooth(entries[i].target);
          }
        })
      : null;

  var enhanceTimer = null;
  function enhance(root) {
    collectTargets(root).forEach(function (el) {
      applySmooth(el);
      if (resizeObserver && observed && !observed.has(el)) {
        observed.add(el);
        resizeObserver.observe(el);
      }
    });
  }

  function scheduleEnhance() {
    if (enhanceTimer) clearTimeout(enhanceTimer);
    enhanceTimer = setTimeout(function () {
      enhance(document);
    }, 50);
  }

  function boot() {
    enhance(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', scheduleEnhance);
  window.addEventListener('resize', scheduleEnhance);

  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(scheduleEnhance);
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
